import { useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { LabelLarge } from '../../elements';
import { useThemeContext } from '../../elements/theme/CustomThemeProvider';
import styled from 'styled-components';
import { ErrorContainer, ErrorIcon, ErrorText } from '../../Pages/BusinessInfo';
import { useDispatch, useSelector } from 'react-redux';
import { policyPurchaseFailed } from '../../actions/quote';

interface StripeCheckoutProps {
  reasonabilityError: string;
  setReasonabilityError: (error: string) => void;
}

const StripeCheckout = ({ reasonabilityError, setReasonabilityError }: StripeCheckoutProps) => {
  const policyPurchaseErrors = useSelector((state: ReduxState) => state.policyPurchaseErrors || []);
  const dispatch = useDispatch();
  const [focusedInput, setFocusedInput] = useState('');
  const [cardErrors, setCardErrors] = useState({
    number: '',
    exp: '',
    cvc: '',
  });
  const { theme } = useThemeContext();

  const inputStyle = {
    fontWeight: '400',
    fontFamily: theme.font.typeface.secondary,
    fontSize: '16px',
    lineHeight: '25px',
  };

  const options = {
    style: {
      base: inputStyle,
      color: theme.colors.primary.zero, // override invalid red color with theme color (invalid inputs have a red border and inline error messaging so text should remain the same)
    },
  };

  const handleChange = ({ elementType, error }: { elementType: string; error?: { message: string } }) => {
    if (reasonabilityError) {
      setReasonabilityError('');
    }
    if (policyPurchaseErrors && policyPurchaseErrors.length > 0) {
      dispatch(policyPurchaseFailed([]));
    }

    if (elementType === 'cardNumber') {
      setCardErrors((prev) => ({ ...prev, number: error?.message || '' }));
    } else if (elementType === 'cardExpiry') {
      setCardErrors((prev) => ({ ...prev, exp: error?.message || '' }));
    } else if (elementType === 'cardCvc') {
      setCardErrors((prev) => ({ ...prev, cvc: error?.message || '' }));
    }
  };

  const handleBlur = () => setFocusedInput('');

  return (
    <div data-testid="stripe-checkout">
      <LabelLarge htmlFor="card_number">Credit card number</LabelLarge>
      <CardInputWrapper hasFocus={focusedInput === 'card_number'} hasError={Boolean(cardErrors.number)}>
        <CardNumberElement
          id="card_number"
          options={{ ...options, placeholder: 'XXXX-XXXX-XXXX-XXXX' }}
          onChange={handleChange}
          onFocus={() => setFocusedInput('card_number')}
          onBlur={handleBlur}
        />
      </CardInputWrapper>
      {cardErrors.number && (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorText>Enter a valid card number</ErrorText>
        </ErrorContainer>
      )}

      <InfoRow>
        <SubRow>
          <LabelLarge htmlFor="card_exp">Expiration</LabelLarge>
          <CardInputWrapper hasFocus={focusedInput === 'card_exp'} hasError={Boolean(cardErrors.exp)}>
            <CardExpiryElement
              id="card_exp"
              options={{ ...options, placeholder: 'MM/YY' }}
              onChange={handleChange}
              onFocus={() => setFocusedInput('card_exp')}
              onBlur={handleBlur}
            />
          </CardInputWrapper>
          {cardErrors.exp && (
            <ErrorContainer>
              <ErrorIcon />
              <ErrorText>Enter a valid expiration date</ErrorText>
            </ErrorContainer>
          )}
        </SubRow>
        <SubRow>
          <LabelLarge htmlFor="card_cvc">CVC</LabelLarge>
          <CardInputWrapper hasFocus={focusedInput === 'card_cvc'} hasError={Boolean(cardErrors.cvc)}>
            <CardCvcElement
              id="card_cvc"
              options={{ ...options, placeholder: 'CVC' }}
              onChange={handleChange}
              onFocus={() => setFocusedInput('card_cvc')}
              onBlur={handleBlur}
            />
          </CardInputWrapper>
          {cardErrors.cvc && (
            <ErrorContainer>
              <ErrorIcon />
              <ErrorText>Enter a valid CVC</ErrorText>
            </ErrorContainer>
          )}
        </SubRow>
      </InfoRow>
      {reasonabilityError ? <ErrorText>{reasonabilityError}</ErrorText> : null}
    </div>
  );
};

export default StripeCheckout;

const CardInputWrapper = styled.div<{ hasFocus: boolean; hasError?: boolean }>`
  background-color: ${({ theme }) => theme.formElements.input.background};
  border: ${({ theme, hasFocus, hasError }) => {
    if (hasError) {
      return `1px solid ${theme.colors.alert.zero}`; // Red border for errors
    } else if (hasFocus) {
      return theme.formElements.input.outlineOnFocus;
    } else {
      return theme.formElements.input.border;
    }
  }};
  padding: 11px 16px;
  border-radius: 4px;
  margin: 8px 0 8px;
`;

const InfoRow = styled.div`
  display: flex;
`;

const SubRow = styled.div`
  flex: 1 1 0%;
  width: 100%;

  &:first-of-type {
    margin-right: 1em;
  }
`;
